/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/no-unstable-nested-components */
import React, { forwardRef, memo, type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { Text, Tooltip } from 'styled-system';

import { useSize } from '../../features/config';
import { Icon, type IconName } from '../Icon';

import { AvatarChip, GroupChip, StatusChip } from './components';

import { type CHIP_STATUS, type ChipGroup, type ChipProps, ChipType, truncateText } from '.';

import styles from './Chip.module.scss';

export const ICON_TEST_ID = 'chip-icon';
const MAX_CHIP_TEXT_LENGTH = 30;

type ChipComponentProps = {
    children: ReactNode;
} & ChipProps;

const ChipComponent = memo(
    forwardRef<HTMLDivElement, ChipComponentProps>(({ children, ...chipProps }, ref) => {
        const {
            className,
            type,
            customIcon,
            text,
            avatar,
            group,
            status,
            statusSize: statusSizeProp,
            background = true,
            noPadding,
            color
        } = chipProps;
        const statusSize = useSize(statusSizeProp, 'large', 'Chip');
        switch (type) {
            case ChipType.Avatar:
                return (
                    <AvatarChip alt={text} avatar={avatar} className={className} ref={ref}>
                        {children}
                    </AvatarChip>
                );
            case ChipType.Group:
                return (
                    <GroupChip
                        background={background}
                        className={className}
                        group={group as ChipGroup}
                        ref={ref}
                    >
                        {children}
                    </GroupChip>
                );
            case ChipType.Status:
                return (
                    <StatusChip
                        background={background}
                        noPadding={noPadding}
                        className={className}
                        color={color}
                        status={status as CHIP_STATUS}
                        statusSize={statusSize}
                        ref={ref}
                    >
                        {children}
                    </StatusChip>
                );
            case ChipType.CustomIcon:
                return (
                    <div className={classnames(styles.chip, className)} ref={ref}>
                        <Icon aria-label={customIcon} name={customIcon as IconName} />
                        {children}
                    </div>
                );
            default:
                return (
                    <div className={classnames(styles.chip, className)} ref={ref}>
                        {children}
                    </div>
                );
        }
    })
);

ChipComponent.displayName = 'ChipComponent';

export const Chip = memo(
    forwardRef<HTMLElement, ChipProps>((props: ChipProps, ref): ReactElement => {
        const {
            icon = 's-icon-xmark',
            maxChipLength = MAX_CHIP_TEXT_LENGTH,
            onClick,
            text: originalText,
            tooltip,
            textRender,
            truncate
        } = props;
        const isNeedTruncate = originalText?.length > maxChipLength;
        const text = isNeedTruncate ? truncateText(originalText) : originalText;

        const chipNode = (
            <ChipComponent {...props}>
                <Text ref={ref} className={classnames(styles['chip-text'])} truncate={truncate}>
                    {textRender ? textRender(text) : text}
                </Text>
                {onClick && icon && (
                    <Icon
                        data-testid={ICON_TEST_ID}
                        aria-label={icon}
                        className={styles['chip-icon']}
                        name={icon}
                        onClick={onClick}
                    />
                )}
            </ChipComponent>
        );

        if (!tooltip && !isNeedTruncate) {
            return chipNode;
        }

        return (
            <Tooltip
                textAlignment="center"
                hidden={tooltip ? false : !isNeedTruncate}
                trigger={['hover', 'focus']}
                title={tooltip || originalText}
            >
                {chipNode}
            </Tooltip>
        );
    })
);

Chip.displayName = 'Chip';
